import React, { useState, useEffect } from "react";
import { Modal, ModalBody, Input, Label, Button, Spinner } from "reactstrap";
import OTPInput from "react-otp-input";
import { useDispatch } from "react-redux";
import { CustomSnackbar } from "../../CustomSnackbar";
import {
  apiErrorHandler,
  validateMobileNumber,
  validateEmail,
} from "../../common";
import {
  changeLoginField,
  changeLoginFieldConfirmation,
} from "../../../api/customer";
import { updateLoginField } from "../../../actions/customer";
import "./styles.css";

const ChangeLoginFieldModal = ({
  openModal,
  setOpenModal,
  field, // "email" or "mobile"
  currentValue,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState("input"); // "input" or "otp"
  const [newValue, setNewValue] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const toggleModal = () => {
    setOpenModal(!openModal);
    // Reset state when modal is closed
    if (openModal) {
      setStep("input");
      setNewValue("");
      setOtp("");
      setLoading(false);
      setOtpLoading(false);
      setRemainingTime(0);
      setCanResend(false);
    }
  };

  // Timer for resend OTP
  useEffect(() => {
    let timer;
    if (remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [remainingTime]);

  const handleSubmitNewValue = async (e) => {
    e.preventDefault();

    if (!newValue) {
      CustomSnackbar.error(
        `Please enter your new ${field === "email" ? "email" : "mobile number"}`
      );
      return;
    }

    if (field === "email") {
      const emailValidation = validateEmail(newValue);
      if (emailValidation !== "") {
        CustomSnackbar.error(emailValidation);
        return;
      }
    }

    if (field === "mobile_number") {
      const mobileValidation = validateMobileNumber(newValue);

      if (mobileValidation !== "") {
        CustomSnackbar.error(mobileValidation);
        return;
      }
    }

    // Check if new value is same as current
    if (newValue === currentValue) {
      CustomSnackbar.error(
        `New ${
          field === "email" ? "email" : "mobile number"
        } cannot be the same as current ${
          field === "email" ? "email" : "mobile number"
        }`
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        field: field,
        new_value: newValue,
      };

      const response = await changeLoginField(payload);

      if (response?.data?.message) {
        CustomSnackbar.success(response.data.message);
        setStep("otp");
        setRemainingTime(60); // Start 60 second timer
        setCanResend(false);
      }
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      CustomSnackbar.error("Please enter OTP");
      return;
    }

    if (otp.length < 6) {
      CustomSnackbar.error("Please enter a valid 6-digit OTP");
      return;
    }

    setOtpLoading(true);
    try {
      const payload = {
        field: field,
        otp: otp,
      };

      const response = await changeLoginFieldConfirmation(payload);

      if (response?.data?.token) {
        const { token, customer } = response.data;

        // Update Redux store with new customer data
        dispatch(updateLoginField({ token, customer }));

        if (onSuccess) {
          onSuccess(newValue);
        }
        toggleModal();
      }
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;

    setOtpLoading(true);
    try {
      const payload = {
        field: field,
        new_value: newValue,
      };

      const response = await changeLoginField(payload);

      if (response?.data?.message) {
        CustomSnackbar.success("OTP resent successfully");
        setRemainingTime(60);
        setCanResend(false);
      }
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setOtpLoading(false);
    }
  };

  const getFieldLabel = () => {
    return field === "email" ? "Email Address" : "Mobile Number";
  };

  const getFieldPlaceholder = () => {
    return field === "email"
      ? "Enter your new email address"
      : "Enter your new mobile number";
  };

  const getFieldType = () => {
    return field === "email" ? "email" : "tel";
  };

  const renderInputStep = () => (
    <div className="change-login-field-container">
      <div className="text-center mb-4">
        <h4 className="change-login-field-title">Change {getFieldLabel()}</h4>
        <p className="change-login-field-subtitle">
          Enter your new {field === "email" ? "email" : "mobile number"}. An OTP
          will be sent for verification.
        </p>
      </div>

      <form onSubmit={handleSubmitNewValue}>
        <div className="mb-3">
          <Label htmlFor="currentValue" className="form-label">
            Current {getFieldLabel()}
          </Label>
          <Input
            type={getFieldType()}
            className="form-control"
            id="currentValue"
            value={currentValue}
            disabled
            readOnly
          />
        </div>

        <div className="mb-4">
          <Label htmlFor="newValue" className="form-label">
            New {getFieldLabel()} *
          </Label>
          <Input
            type={getFieldType()}
            className="form-control"
            id="newValue"
            placeholder={getFieldPlaceholder()}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            required
          />
        </div>

        <div className="text-center">
          {loading ? (
            <Button color="primary" className="w-100" disabled>
              <Spinner size="sm" className="me-2" />
              Sending OTP...
            </Button>
          ) : (
            <Button color="primary" className="w-100" type="submit">
              Send OTP
            </Button>
          )}
        </div>
      </form>
    </div>
  );

  const renderOtpStep = () => (
    <div className="change-login-field-container">
      <div className="text-center mb-4">
        <h4 className="change-login-field-title">Verify OTP</h4>
        <p className="change-login-field-subtitle">
          An OTP has been sent to <b>{currentValue}</b>. Please enter the 6-digit
          code to verify your new{" "}
          {field === "email" ? "email" : "mobile number"}.
        </p>
      </div>

      <div className="otp-container mb-4">
        <OTPInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span style={{ margin: 4 }}>-</span>}
          containerStyle={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: "3px",
            marginBottom: "15px",
            padding: "0 5px",
            maxWidth: "100%",
          }}
          inputType="number"
          renderInput={(props) => (
            <input {...props} className="otp-input" style={{ flexShrink: 0 }} />
          )}
        />
      </div>

      <div className="resend-otp-container mb-4">
        {remainingTime > 0 ? (
          <p className="text-center text-muted">
            You can resend the OTP in{" "}
            <span className="fw-bold">{remainingTime}</span> seconds
          </p>
        ) : (
          <div className="text-center">
            <Button
              color="link"
              className="p-0"
              onClick={handleResendOtp}
              disabled={otpLoading}
            >
              {otpLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Resending...
                </>
              ) : (
                "Resend OTP"
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="text-center">
        {otpLoading ? (
          <Button color="primary" className="w-100" disabled>
            <Spinner size="sm" className="me-2" />
            Verifying...
          </Button>
        ) : (
          <Button color="primary" className="w-100" onClick={handleVerifyOtp}>
            Verify & Update
          </Button>
        )}
      </div>

      <div className="text-center mt-3">
        <Button color="link" className="p-0" onClick={() => setStep("input")}>
          ← Back to change {field === "email" ? "email" : "mobile number"}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal isOpen={openModal} toggle={toggleModal} centered size="md">
      <ModalBody className="modal-body p-4">
        <div className="position-absolute end-0 top-0 p-3">
          <button
            type="button"
            className="btn-close"
            onClick={toggleModal}
            aria-label="Close"
          ></button>
        </div>

        {step === "input" ? renderInputStep() : renderOtpStep()}
      </ModalBody>
    </Modal>
  );
};

export default ChangeLoginFieldModal;
